import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Alert,
  Card,
  CardContent,
  CardMedia,
} from '@mui/material';
import PromptSelector from '../components/PromptSelector';
import ScriptEditor from '../components/ScriptEditor';
import { getScriptTemplates } from '../../../apis/apiList/publicAiStudioApi';

const WriteScriptStep = ({
  selectedAvatar,
  selectedLook,
  selectedLookData,
  generatedLook,
  script,
  onScriptChange,
}) => {
  const [templates] = useState(getScriptTemplates());
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    // Auto-save to localStorage
    if (script) {
      localStorage.setItem('ai-studio-draft-script', script);
    }
  }, [script]);

  useEffect(() => {
    // Load draft from localStorage on mount
    const draft = localStorage.getItem('ai-studio-draft-script');
    if (draft && !script) {
      onScriptChange(draft);
    }
  }, []);

  const handleTemplateSelect = (value) => {
    setSelectedTemplate(value);
    onScriptChange(value);
  };

  const handleScriptChange = (value) => {
    onScriptChange(value);
    if (value.length >= 20 && value.length <= 1000) {
      setError('');
    } else if (value.length > 0 && value.length < 20) {
      setError('Script must be at least 20 characters');
    } else if (value.length > 1000) {
      setError('Script must be less than 1000 characters');
    }
  };

  const isScriptValid = script.length >= 20 && script.length <= 1000;

  const selectedLookImage =
    selectedLook === 'generated'
      ? generatedLook?.image
      : selectedLook === 'existing'
      ? selectedLookData?.imageUrl
      : selectedAvatar?.image;

  const lookTitle =
    selectedLook === 'generated'
      ? 'Custom Generated Look'
      : selectedLook === 'existing'
      ? 'Selected Generated Look'
      : 'Original Avatar';

  const lookDescription =
    selectedLook === 'generated'
      ? generatedLook?.prompts
      : selectedLook === 'existing'
      ? selectedLookData?.prompts
      : 'Base avatar look from your selected avatar.';

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Write Your Script
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Create the message your avatar will deliver in the video.
      </Typography>

      <Card sx={{ mb: 3, p: 3, borderRadius: 3 }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 3,
            alignItems: 'flex-start',
          }}
        >
          <Card
            sx={{
              minWidth: 260,
              width: 260,
              borderRadius: 3,
              overflow: 'hidden',
            }}
          >
            <Box sx={{ position: 'relative' }}>
              <CardMedia
                component="img"
                height="320"
                image={`${selectedLookImage}`}
                alt={lookTitle}
                sx={{ objectFit: 'cover',
                  height: {
                    xs: 230,
                    sm: 230,
                    md: 270,
                  }
                }}
              />
            </Box>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {lookTitle}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {lookDescription}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                This is the look your script will be paired with.
              </Typography>
            </CardContent>
          </Card>

          <Box sx={{ flex: 1, minWidth: 320 }}>
            <PromptSelector
              prompts={templates}
              selectedPrompt={selectedTemplate}
              onSelect={handleTemplateSelect}
              label="Choose a Script"
              helperText="Select a Script as a starting point or write your own script"
            />

            <ScriptEditor
              value={script}
              onChange={handleScriptChange}
              minLength={20}
              maxLength={1000}
              error={!!error}
              helperText={error}
              sx={{ mt: 2 }}
            />

            {!isScriptValid && script.length > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {script.length < 20
                  ? `Add ${20 - script.length} more characters to meet the minimum requirement`
                  : 'Script is too long. Please reduce it to 1000 characters or less'}
              </Alert>
            )}

            {isScriptValid && (
              <Alert severity="success" sx={{ mt: 2 }}>
                ✓ Script looks good! You can proceed to generate your video.
              </Alert>
            )}

            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                💡 Tip: Your script is automatically saved as a draft
              </Typography>
            </Box>
          </Box>
        </Box>
      </Card>
    </Box>
  );
};

export default WriteScriptStep;
